import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CANVAS_UI, FLOATING_BAR } from './canvas-ui';

// Wraps rather than overflow: a member's title pill is wider than a phone (P5d).
const bar = `pointer-events-auto flex min-h-11 max-w-full flex-wrap items-center gap-2 rounded-3xl px-2 py-1.5 font-sans text-sm text-fg ${FLOATING_BAR}`;

export type TopBarSlot = 'menu' | 'page' | 'presence' | 'comments';
type Slots = {
  elements: Partial<Record<TopBarSlot, HTMLElement | null>>;
  register: (slot: TopBarSlot, el: HTMLElement | null) => void;
  /** How many layers currently draw in the right bar: an empty right bar stays hidden. */
  filled: number;
  claim: () => () => void;
};
const SlotsContext = createContext<Slots | null>(null);

/** Lets layers drawn inside the editor (main menu, presence, comments) place their buttons in the route's top bar. */
export function TopBarProvider({ children }: { children: ReactNode }) {
  const [elements, setElements] = useState<Slots['elements']>({});
  const [filled, setFilled] = useState(0);
  const register = useCallback((slot: TopBarSlot, el: HTMLElement | null) => setElements((cur) => (cur[slot] === el ? cur : { ...cur, [slot]: el })), []);
  const claim = useCallback(() => {
    setFilled((n) => n + 1);
    return () => setFilled((n) => n - 1);
  }, []);
  const value = useMemo(() => ({ elements, register, filled, claim }), [elements, register, filled, claim]);
  return <SlotsContext.Provider value={value}>{children}</SlotsContext.Provider>;
}

/** Renders `children` in the top bar's slot when there is one, else `fallback` (its own floating position). */
export function InTopBar({ slot, children, fallback }: { slot: TopBarSlot; children: ReactNode; fallback: ReactNode }) {
  const slots = useContext(SlotsContext);
  const el = slots?.elements[slot];
  const claim = slots?.claim;
  // The main menu sits on the left: only right-side layers reveal the right bar.
  useLayoutEffect(() => (el && claim && slot !== 'menu' && slot !== 'page' ? claim() : undefined), [el, claim, slot]);
  return el ? createPortal(children, el) : fallback;
}

/**
 * The editor's top corners (spec §H.4): left = main menu and board name; right (backlog §19 P16-3.2) = my role, the
 * people (members panel) | comments, history. Two floating bars that leave the canvas between them free.
 */
export function TopBar({ left, lead, right }: { left?: ReactNode; lead?: ReactNode; right?: ReactNode }) {
  const slots = useContext(SlotsContext);
  const register = slots?.register;
  // Stable ref callbacks: a new function each render would unregister and register the slot again, in a loop.
  const menuRef = useCallback((el: HTMLElement | null) => register?.('menu', el), [register]);
  const pageRef = useCallback((el: HTMLElement | null) => register?.('page', el), [register]);
  const presenceRef = useCallback((el: HTMLElement | null) => register?.('presence', el), [register]);
  const commentsRef = useCallback((el: HTMLElement | null) => register?.('comments', el), [register]);
  return (
    <div {...{ [CANVAS_UI]: '' }} data-topbar="" data-fit-inset="" className="pointer-events-none absolute inset-x-3 top-3 z-20 flex flex-wrap items-start justify-between gap-2">
      {left ? (
        <div role="group" className={bar}>
          {slots && <span ref={menuRef} className="contents" />}
          {left}
          {/* The page chip (P14), right after the board's name. */}
          {slots && <span ref={pageRef} className="contents" />}
        </div>
      ) : (
        <span />
      )}
      {(right || lead || slots) && (
        // Rendered even when empty so the slots exist for the layers; hidden until one of them (or `right`) fills it.
        <div role="group" hidden={!right && !lead && !slots?.filled} className={bar}>
          {lead}
          {slots && <span ref={presenceRef} className="contents" />}
          {slots && <span data-separator="" aria-hidden="true" className="h-5 w-px bg-border" />}
          {slots && <span ref={commentsRef} className="contents" />}
          {right}
        </div>
      )}
    </div>
  );
}
